import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    service: '',
    time: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    notes: '',
  });

  const services = [
    '1-Step Paint Correction',
    '2-Step Paint Correction',
    'Scratch Removal',
    'Touch-Up Paint',
    'Ceramic Coating',
    'Headlight Restoration',
    'Interior Deep Cleaning',
    'Exterior Wash + Tire Shine',
    'Full Vehicle Polishing Package',
  ];

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.service) {
      toast.error('Please select a service');
      return;
    }

    if (!formData.time) {
      toast.error('Please select a time');
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking submitted successfully! Check your email for confirmation.');
      
      // Reset form
      setFormData({
        service: '',
        time: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: '',
        notes: '',
      });
      setDate(null);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="min-h-screen bg-black">
      <Navigation />

      {/* Hero Section */}
      <section data-testid="booking-hero" className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 
            data-testid="booking-hero-title"
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Book Your <span className="text-red-500">Appointment</span>
          </h1>
          <p data-testid="booking-hero-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
            Schedule your mobile detailing service at your preferred date and time
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section data-testid="booking-form-section" className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} data-testid="booking-form" className="glass-card p-8 md:p-12 space-y-6">
            {/* Service Selection */}
            <div data-testid="service-select-container" className="space-y-2">
              <Label htmlFor="service" className="text-gray-200">Select Service *</Label>
              <Select value={formData.service} onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}>
                <SelectTrigger data-testid="service-select-trigger" className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent data-testid="service-select-content" className="bg-gray-900 border-gray-700">
                  {services.map((service, index) => (
                    <SelectItem key={index} value={service} data-testid={`service-option-${index}`} className="text-white hover:bg-gray-800">
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div data-testid="date-select-container" className="space-y-2">
              <Label className="text-gray-200">Select Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="date-picker-trigger"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-gray-900 border-gray-700 text-white hover:bg-gray-800"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent data-testid="date-picker-calendar" className="w-auto p-0 bg-gray-900 border-gray-700">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date()}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selection */}
            <div data-testid="time-select-container" className="space-y-2">
              <Label htmlFor="time" className="text-gray-200">Select Time *</Label>
              <Select value={formData.time} onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}>
                <SelectTrigger data-testid="time-select-trigger" className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent data-testid="time-select-content" className="bg-gray-900 border-gray-700">
                  {timeSlots.map((time, index) => (
                    <SelectItem key={index} value={time} data-testid={`time-option-${index}`} className="text-white hover:bg-gray-800">
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Client Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-testid="client-name-container" className="space-y-2">
                <Label htmlFor="client_name" className="text-gray-200">Your Name *</Label>
                <Input
                  id="client_name"
                  name="client_name"
                  data-testid="client-name-input"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  required
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="John Doe"
                />
              </div>

              <div data-testid="client-email-container" className="space-y-2">
                <Label htmlFor="client_email" className="text-gray-200">Email Address *</Label>
                <Input
                  id="client_email"
                  name="client_email"
                  data-testid="client-email-input"
                  type="email"
                  value={formData.client_email}
                  onChange={handleInputChange}
                  required
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div data-testid="client-phone-container" className="space-y-2">
              <Label htmlFor="client_phone" className="text-gray-200">Phone Number *</Label>
              <Input
                id="client_phone"
                name="client_phone"
                data-testid="client-phone-input"
                value={formData.client_phone}
                onChange={handleInputChange}
                required
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="(929) 774-6344"
              />
            </div>

            {/* Vehicle Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div data-testid="vehicle-year-container" className="space-y-2">
                <Label htmlFor="vehicle_year" className="text-gray-200">Year *</Label>
                <Input
                  id="vehicle_year"
                  name="vehicle_year"
                  data-testid="vehicle-year-input"
                  value={formData.vehicle_year}
                  onChange={handleInputChange}
                  required
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="2020"
                />
              </div>

              <div data-testid="vehicle-make-container" className="space-y-2">
                <Label htmlFor="vehicle_make" className="text-gray-200">Make *</Label>
                <Input
                  id="vehicle_make"
                  name="vehicle_make"
                  data-testid="vehicle-make-input"
                  value={formData.vehicle_make}
                  onChange={handleInputChange}
                  required
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Honda"
                />
              </div>

              <div data-testid="vehicle-model-container" className="space-y-2">
                <Label htmlFor="vehicle_model" className="text-gray-200">Model *</Label>
                <Input
                  id="vehicle_model"
                  name="vehicle_model"
                  data-testid="vehicle-model-input"
                  value={formData.vehicle_model}
                  onChange={handleInputChange}
                  required
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Accord"
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div data-testid="notes-container" className="space-y-2">
              <Label htmlFor="notes" className="text-gray-200">Additional Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                data-testid="notes-textarea"
                value={formData.notes}
                onChange={handleInputChange}
                className="bg-gray-900 border-gray-700 text-white min-h-[100px]"
                placeholder="Any specific requests or information we should know..."
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white py-6 text-lg font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Confirm Booking'
              )}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingPage;
